﻿import { bootstrapSheet } from '../../core/styles/bootstrap.js';
import { eumBaseSheet } from '../../core/styles/eum-base.js';
import { i18n } from '../../core/i18n/i18n-service.js';
// @ts-ignore — handled by esbuild inline-css plugin
import componentSheet from './eum-search-box.css';

/**
 * <eum-search-box> — text input with clear button.
 * Fires `eum-search` CustomEvent with { searchText: string } on Enter or click.
 */
export class EumSearchBox extends HTMLElement {
    private input!: HTMLInputElement;
    private clearBtn!: HTMLButtonElement;

    static get observedAttributes(): string[] {
        return ['search-text'];
    }

    async connectedCallback(): Promise<void> {
        const shadow = this.attachShadow({ mode: 'open' });
        shadow.adoptedStyleSheets = [bootstrapSheet, eumBaseSheet, componentSheet];

        shadow.innerHTML = /*html*/ `
            <div class="input-group">
                <input type="text" class="form-control" placeholder="${this.escapeAttr(i18n.t('searchBox.placeholder'))}" autocomplete="off" />
                <button class="btn btn-clear" type="button" title="${this.escapeAttr(i18n.t('searchBox.clearLabel'))}" hidden>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="bi bi-x" viewBox="0 0 16 16">
                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"></path>
                    </svg>
                </button>
            </div>
        `;

        this.input = shadow.querySelector('input')!;
        this.clearBtn = shadow.querySelector('button')!;
        this.input.value = this.getAttribute('search-text') ?? '';
        this.toggleClearButton();

        this.input.addEventListener('keyup', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                this.fireSearch();
            }
        });

        this.input.addEventListener('input', () => {
            this.toggleClearButton();
        });

        this.clearBtn.addEventListener('click', () => {
            this.input.value = '';
            this.toggleClearButton();
            this.fireSearch();
        });
    }

    attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
        if (name !== 'search-text' || oldValue === newValue || !this.input) return;

        this.input.value = newValue ?? '';
        this.toggleClearButton();
    }

    /** Current text in the input (trimmed on read is the caller's concern). */
    get value(): string {
        return this.input?.value ?? '';
    }

    /**
     * Reset the input back to empty. Unlike setting the `search-text`
     * attribute, this always clears the live input value even when the user
     * typed directly (which never mutates the attribute), so callers can
     * reliably return the control to its initial state.
     */
    clear(): void {
        if (!this.input) return;
        this.input.value = '';
        this.toggleClearButton();
    }

    private toggleClearButton(): void {
        this.clearBtn.hidden = this.input.value.length === 0;
    }

    private escapeAttr(str: string): string {
        return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    }

    private fireSearch(): void {
        this.dispatchEvent(
            new CustomEvent('eum-search', {
                bubbles: true,
                composed: true,
                detail: { searchText: this.input.value.trim() },
            })
        );
    }
}

customElements.define('eum-search-box', EumSearchBox);